/**
 * LE SCEAU DE FINALISATION — LA RÈGLE, SANS LE DOM.
 *
 * Module PUR, comme `entry-motion.ts` : il décide QUAND chaque temps du sceau
 * a lieu, et si le sceau se joue. Le dessin vit dans `sceau.css`, la mise en
 * scène dans `SceauHost.tsx` ; ici, seulement des nombres — ceux qu'un test
 * peut vérifier sans navigateur.
 *
 * ═══ LES DURÉES VIENNENT DES JETONS ═══
 * Le minuteur lit les jetons VIVANTS (`--dur-*`) : le mouvement réduit les
 * raccourcit (intensity.css), le CSS et le JS restent donc d'accord. Les
 * valeurs par défaut ci-dessous sont celles de tokens.css, et un test le
 * vérifie : un jeton qui change ne laisse pas le minuteur derrière lui.
 *
 * ═══ CE QUI N'EST PAS DU MOUVEMENT ═══
 * En mouvement réduit, le sceau ne bouge plus mais il se LIT : 700 ms au
 * moins, 1 s au plus. Ce sont des temps de lecture (`LECTURE_REDUITE`), pas
 * des durées d'animation — ils ne suivent donc pas les jetons.
 */

import type { MotionLevel } from './entry-motion.js'

/** Les cinq jetons de durée dont le sceau a besoin, en millisecondes. */
export interface JetonsSceau {
  instant: number
  hover: number
  nav: number
  card: number
  count: number
}

/** Les valeurs de tokens.css (`--dur-instant`, `--dur-hover`, `--dur-nav`, `--dur-card`, `--dur-count`). */
export const JETONS_DEFAUT: JetonsSceau = { instant: 120, hover: 200, nav: 320, card: 420, count: 900 }

/** Temps de LECTURE du sceau en mouvement réduit — pas des durées de mouvement. */
export const LECTURE_REDUITE = { min: 3000, max: 3300 } as const

/** Filets de sécurité : quoi qu'il arrive, le produit est rendu à ce moment-là. */
export const FILET_PLEIN = 4400
/**
 * La tenue du sceau : il reste à l'écran au moins 3 s avant que le voile se
 * lève (demande de l'exploitant, 11/10/2026 : « il faudrait que ça reste au
 * moins 3 secondes »). Un clic ou Échap le passent toujours.
 */
export const TENUE_SCEAU = 3000
export const FILET_REDUIT = 3900

/** « 420ms », « 0.42s » → 420 ; illisible → null. */
export function dureeEnMs(valeur: string): number | null {
  const m = /^(\d*\.?\d+)(ms|s)$/.exec(valeur.trim())
  if (!m) return null
  const n = Number(m[1])
  if (!Number.isFinite(n)) return null
  return m[2] === 's' ? n * 1000 : n
}

/** Les jetons VIVANTS de l'élément racine ; chacun retombe sur sa valeur par défaut s'il est illisible. */
export function lireJetons(root: Element | null): JetonsSceau {
  if (!root || typeof getComputedStyle !== 'function') return { ...JETONS_DEFAUT }
  const style = getComputedStyle(root)
  const lire = (nom: string, defaut: number): number => dureeEnMs(style.getPropertyValue(nom)) ?? defaut
  return {
    instant: lire('--dur-instant', JETONS_DEFAUT.instant),
    hover: lire('--dur-hover', JETONS_DEFAUT.hover),
    nav: lire('--dur-nav', JETONS_DEFAUT.nav),
    card: lire('--dur-card', JETONS_DEFAUT.card),
    count: lire('--dur-count', JETONS_DEFAUT.count),
  }
}

export type NiveauSceau = MotionLevel

/**
 * Le niveau de mouvement du sceau. La préférence du SYSTÈME l'emporte même
 * quand celle de l'application dit « complet » : le CSS (@media) réduit déjà
 * dans ce cas, et le minuteur doit raconter la même histoire que lui.
 *
 * Les deux entrées sont lues par l'appelant (`motionLevel()` d'auth-motion.ts
 * et `matchMedia`) : ce module reste pur.
 */
export function niveauSceau(preference: MotionLevel, systemeReduit: boolean): NiveauSceau {
  if (preference === 'minimal') return 'minimal'
  if (preference === 'reduced' || systemeReduit) return 'reduced'
  return 'full'
}

/**
 * La séquence, en millisecondes depuis la réponse 2xx du serveur (t0).
 *
 *   scene       le sceau se joue-t-il (minimal ou onglet caché : non)
 *   vol         le sceau vole-t-il jusqu'à la pastille « Clôturée »
 *   bascule     la page change SOUS le voile opaque
 *   leveeMin    le voile ne se lève pas avant (le geste doit être lisible)
 *   leveeMax    ni après (une page lente ne retient pas le produit)
 *   assise      délai après l'apparition de la page avant de la mesurer
 *   arrivee     après la levée : l'accent de la pastille
 *   demontage   après la levée : le sceau disparaît, le focus se pose
 *   nettoyage   après la levée : la dernière classe est retirée
 *   filet       depuis t0 : tout est rendu, quoi qu'il arrive
 */
export interface SequenceSceau {
  scene: boolean
  vol: boolean
  bascule: number
  leveeMin: number
  leveeMax: number
  assise: number
  arrivee: number
  demontage: number
  nettoyage: number
  filet: number
}

export function sequenceSceau(niveau: NiveauSceau, ongletCache: boolean, j: JetonsSceau = JETONS_DEFAUT): SequenceSceau {
  if (niveau === 'minimal' || ongletCache) {
    return { scene: false, vol: false, bascule: 0, leveeMin: 0, leveeMax: 0, assise: 0, arrivee: 0, demontage: 0, nettoyage: 0, filet: 0 }
  }
  if (niveau === 'reduced') {
    return {
      scene: true, vol: false,
      bascule: j.instant,
      leveeMin: LECTURE_REDUITE.min, leveeMax: LECTURE_REDUITE.max,
      assise: 0, arrivee: 0,
      demontage: j.nav, nettoyage: j.nav,
      filet: Math.max(FILET_REDUIT, LECTURE_REDUITE.max + j.nav + 200),
    }
  }
  const leveeMin = Math.max(j.card + j.nav + j.hover, TENUE_SCEAU)
  const leveeMax = leveeMin + j.nav
  const nettoyage = j.card + j.nav
  return {
    scene: true, vol: true,
    // Le sceau est monté (C) : la page peut changer dessous.
    bascule: j.card,
    // Le cercle, la coche, le titre, puis la frappe (C + N + H) : le geste est lu.
    leveeMin,
    leveeMax,
    // `.fnb-cadre` entre en `lc-rise` (C) : N de plus et il ne bouge plus d'un pixel.
    assise: j.nav,
    arrivee: j.nav,
    demontage: j.card,
    nettoyage,
    filet: Math.max(FILET_PLEIN, leveeMax + nettoyage + 200),
  }
}

/**
 * Le moment de la levée : la page d'arrivée est prête (`pretA`) et assise,
 * mais jamais avant `leveeMin` ni après `leveeMax`. Une page jamais prête
 * lève à `leveeMax`.
 */
export function momentDeLevee(seq: SequenceSceau, pretA: number | null): number {
  if (pretA === null) return seq.leveeMax
  return Math.min(Math.max(seq.leveeMin, pretA + seq.assise), seq.leveeMax)
}

/** Une boîte à l'écran — un DOMRect suffit. */
export interface Boite { left: number; top: number; width: number; height: number }
export interface Vue { largeur: number; hauteur: number }

/** Le centre du sceau au repos : milieu de l'écran, un peu au-dessus (42 %). */
export const REPOS = { x: 0.5, y: 0.42 } as const

/**
 * D'où le sceau part : le centre du bouton qui l'a déclenché. Sans origine
 * utilisable, il naît sur place, à peine plus petit.
 */
export function departMontee(origine: Boite | null, vue: Vue): { dx: number; dy: number; s0: number } {
  if (!origine || origine.width <= 0 || origine.height <= 0) return { dx: 0, dy: 0, s0: 0.92 }
  const cx = origine.left + origine.width / 2
  const cy = origine.top + origine.height / 2
  if (cx < 0 || cy < 0 || cx > vue.largeur || cy > vue.hauteur) return { dx: 0, dy: 0, s0: 0.92 }
  return { dx: cx - vue.largeur * REPOS.x, dy: cy - vue.hauteur * REPOS.y, s0: 0.4 }
}

/**
 * Le vol est-il possible : une ancre qui existe, qui a une taille, et qui est
 * ENTIÈREMENT à l'écran. Sinon le sceau s'efface sur place.
 */
export function volPossible(glyphe: Boite | null, ancre: Boite | null, vue: Vue): boolean {
  if (!glyphe || !ancre) return false
  if (glyphe.width <= 0 || glyphe.height <= 0 || ancre.width <= 0 || ancre.height <= 0) return false
  return ancre.left >= 0 && ancre.top >= 0
    && ancre.left + ancre.width <= vue.largeur
    && ancre.top + ancre.height <= vue.hauteur
}

/** Le vecteur du vol : du centre du sceau au centre de l'ancre, et le rapport de taille. */
export function vecteurVol(glyphe: Boite, ancre: Boite): { vx: number; vy: number; vs: number } {
  return {
    vx: ancre.left + ancre.width / 2 - (glyphe.left + glyphe.width / 2),
    vy: ancre.top + ancre.height / 2 - (glyphe.top + glyphe.height / 2),
    vs: ancre.width / glyphe.width,
  }
}
